import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { getParticipantFormInitialValues, updateParticipant, } from '../../components/participant_form/helpers/participant_form.helpers';
import { getApiErrorMessage } from '../../helpers/api.helpers';
import { useAxonAccountContext } from '../use_axon_account_context';
import { useCurrentEnvironment } from '../use_current_environment';
import { useForm } from '../use_form';
import { useOrgContext } from '../use_org_context';
import { useSiteContext } from '../use_site_context';
export const useToggleParticipantCaregiverEpro = (participant) => {
    const translate = useLocalizeMessage();
    const org = useOrgContext();
    const account = useAxonAccountContext();
    const { environment } = useCurrentEnvironment();
    const site = useSiteContext();
    const queryClient = useQueryClient();
    const { addNotification } = useToastNotification();
    const { form } = useForm({
        initialValues: getParticipantFormInitialValues(participant),
    });
    const isCaregiverEproEnabled = useMemo(() => {
        return (org?.c_caregiver_epro_enabled ?? false) && (participant?.c_caregiver_epro_enabled ?? false);
    }, [participant, org]);
    const updateParticipantMutation = useMutation({
        mutationFn: updateParticipant,
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['participantDetails', participant?._id] });
            addNotification({
                title: '',
                subtitle: isCaregiverEproEnabled
                    ? translate('status.caregiver_epro_disabled.success')
                    : translate('status.caregiver_epro_enabled.success'),
                type: 'success',
            });
        },
        onError: (e) => {
            const message = getApiErrorMessage(e, true);
            addNotification({
                title: '',
                subtitle: message ?? translate('error.generic'),
                type: 'error',
            });
        },
    });
    const toggleCaregiverEpro = () => {
        if (!environment) {
            throw new Error('Environment not defined');
        }
        if (!participant) {
            throw new Error('Participant not defined');
        }
        updateParticipantMutation.mutate({
            environment,
            account,
            site,
            participantId: participant?._id,
            form: { language: form.language, caregiverEproEnabled: !isCaregiverEproEnabled },
        });
    };
    return {
        isTogglingCaregiverEpro: updateParticipantMutation.isPending,
        isCaregiverEproEnabled,
        toggleCaregiverEpro,
    };
};
